import { NotificationType } from '@/app/shared/interfaces/notification.interface';

/**
 * Shared utility functions for notification components
 * Centralizes common notification-related operations to avoid code duplication
 */
export class NotificationHelpers {
    /**
     * Maps notification types to their corresponding PrimeNG icon classes
     * @param type - The notification type enum value
     * @returns PrimeNG icon class string with color
     */
    static getNotificationIcon(type: NotificationType | string): string {
        const iconMap: Record<string, string> = {
            // Document related
            DOCUMENT_UPLOAD: 'pi pi-file-upload text-blue-500',
            DOCUMENT_APPROVED: 'pi pi-check-circle text-green-500',
            DOCUMENT_REJECTED: 'pi pi-times-circle text-red-500',
            DOCUMENT_VERSION_UPDATE: 'pi pi-refresh text-blue-500',
            DOCUMENT_REVIEW_REQUEST: 'pi pi-eye text-purple-500',
            DOCUMENT_COMMENT: 'pi pi-comment text-gray-500',
            DOCUMENT_SHARED: 'pi pi-share-alt text-blue-500',

            // Client/Consultant access related
            CLIENT_ACCESS_GRANTED: 'pi pi-key text-purple-500',
            CONSULTANT_ASSIGNED: 'pi pi-user-plus text-indigo-500',

            // Disposition related
            DISPOSITION_CREATED: 'pi pi-exclamation-triangle text-orange-500',
            DISPOSITION_FINISHED: 'pi pi-check-square text-green-500',
            DISPOSITION_UPDATED: 'pi pi-pencil text-yellow-500',

            // Template related
            TEMPLATE_INGESTED: 'pi pi-file-import text-cyan-500',
            TEMPLATE_UPDATED: 'pi pi-file-edit text-cyan-500',

            // Warehouse & Lab related
            WAREHOUSE_ALERT: 'pi pi-warehouse text-red-500',
            LAB_ALERT: 'pi pi-shield text-red-500',

            // Batch & Product related
            BATCH_RELEASED: 'pi pi-check text-green-500',
            BATCH_REJECTED: 'pi pi-ban text-red-500',
            PRODUCT_UPDATED: 'pi pi-box text-blue-500',

            // Deviation & Change Control
            DEVIATION_CREATED: 'pi pi-flag text-red-500',
            DEVIATION_ALERT: 'pi pi-exclamation-circle text-red-500',
            CHANGE_CONTROL_CREATED: 'pi pi-cog text-gray-500',
            QUALITY_EXCEPTION: 'pi pi-exclamation-triangle text-red-500',

            // System Security
            DEVICE_AUTH_ALERT: 'pi pi-mobile text-red-500',
            LOGIN_ACTIVITY: 'pi pi-sign-in text-blue-500',
            PASSWORD_CHANGE: 'pi pi-lock text-green-500',
            PASSWORD_RESET: 'pi pi-unlock text-orange-500',
            FAILED_LOGIN_ATTEMPT: 'pi pi-times text-red-500',
            PERMISSION_CHANGE: 'pi pi-key text-purple-500',
            ACCOUNT_LOCKOUT: 'pi pi-ban text-red-500',
            USER_ROLE_MODIFIED: 'pi pi-user-edit text-blue-500',
            SYSTEM_CONFIG_CHANGE: 'pi pi-cog text-gray-500',
            SUSPICIOUS_ACTIVITY: 'pi pi-exclamation-triangle text-red-500',

            // Project Lifecycle
            PROJECT_CREATED: 'pi pi-plus text-green-500',
            PROJECT_DRAFT_APPROVAL: 'pi pi-file-check text-blue-500',
            PROJECT_STATUS_CHANGE: 'pi pi-refresh text-blue-500',
            PROJECT_TIMELINE_UPDATE: 'pi pi-calendar text-purple-500',
            PROJECT_DEADLINE_WARNING: 'pi pi-clock text-orange-500',
            PROJECT_COMPLETED: 'pi pi-check text-green-500',

            // Task Management
            TASK_ASSIGNED: 'pi pi-clipboard text-blue-500',
            TASK_REASSIGNED: 'pi pi-users text-indigo-500',
            TASK_PRIORITY_CHANGE: 'pi pi-arrow-up text-orange-500',
            TASK_DUE_DATE_CHANGE: 'pi pi-calendar text-purple-500',
            TASK_STATUS_UPDATE: 'pi pi-sync text-blue-500',
            TASK_COMPLETED: 'pi pi-check text-green-500',
            TASK_COMMENT: 'pi pi-comment text-gray-500',

            // Sample Management
            SAMPLE_REQUEST: 'pi pi-send text-blue-500',
            SAMPLE_COLLECTION: 'pi pi-inbox text-green-500',
            LAB_TEST_STATUS: 'pi pi-spinner text-blue-500',
            TEST_RESULT_AVAILABLE: 'pi pi-check-square text-green-500',
            OUT_OF_SPEC_ALERT: 'pi pi-exclamation-triangle text-red-500',
            SAMPLE_READY_FOR_INSPECTION: 'pi pi-eye text-orange-500',

            // Collaboration
            MENTION: 'pi pi-at text-indigo-500',
            DISCUSSION_THREAD_UPDATE: 'pi pi-comments text-blue-500',
            COMMENT_REPLY: 'pi pi-reply text-gray-500',
            COMMENT_REACTION: 'pi pi-heart text-pink-500',
            TEAM_ASSIGNMENT: 'pi pi-users text-purple-500',

            // System notifications
            SYSTEM_ANNOUNCEMENT: 'pi pi-megaphone text-purple-500',
        };

        return iconMap[type] || 'pi pi-bell text-gray-500';
    }

    /**
     * Converts a date to a human-readable "time ago" format
     * @param date - The date to convert
     * @returns Human-readable time ago string (e.g., "5m ago", "2h ago")
     */
    static getTimeAgo(date: Date): string {
        const now = new Date();
        const seconds = Math.floor((now.getTime() - new Date(date).getTime()) / 1000);

        if (seconds < 60) return 'Just now';
        if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
        if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
        if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
        return new Date(date).toLocaleDateString();
    }

    /**
     * Formats a priority value for display
     * @param priority - The priority level
     * @returns Formatted priority string
     */
    static getPriorityLabel(priority: string): string {
        return priority.toUpperCase();
    }

    /**
     * Maps priority levels to PrimeNG severity values
     * @param priority - The priority level
     * @returns PrimeNG severity string
     */
    static getPrioritySeverity(priority: string): 'success' | 'info' | 'warning' | 'danger' | 'secondary' {
        const severityMap: Record<string, 'success' | 'info' | 'warning' | 'danger' | 'secondary'> = {
            urgent: 'danger',
            high: 'warning',
            normal: 'info',
            low: 'secondary',
        };
        return severityMap[priority] || 'info';
    }

    /**
     * Gets the display label for a notification type
     * @param type - The notification type
     * @returns Human-readable type label
     */
    static getTypeLabel(type: string): string {
        // Convert SNAKE_CASE to Title Case
        return type
            .split('_')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    }

    /**
     * Formats a date to a full timestamp string
     * @param date - The date to format
     * @returns Formatted timestamp string (e.g., "Jan 15, 2025 at 3:45 PM")
     */
    static getFullTimestamp(date: Date | null): string {
        if (!date) return 'N/A';
        const d = new Date(date);
        return d.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric', 
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    }
}

